import React from 'react';
import { Lightbulb, RefreshCw, Target, Sparkles, BarChart3, Users, MessageSquare, AlertTriangle, CheckCircle, Copy, Loader2 } from 'lucide-react';
import { FacilitatorGuidance } from '../services/workshop';
import { AVATARS } from '../types';
import { FacilitatorChatPanel } from './facilitator-chat/FacilitatorChatPanel';

const formatRole = (role: string) => role.replace(/_/g, ' ');

interface FacilitatorGuidancePanelProps {
  guidance: FacilitatorGuidance | null;
  loading: boolean;
  onRefresh: () => void;
  onCopyPrompt: (prompt: string) => void;
  currentPhase: {
    id: string;
    name: string;
    objectives: string;
    goal?: string;
    successCriteria?: string[];
  };
  // Chat panel props
  sessionId: string;
  transcript: any[];
  sessionAvatars: any[];
  requirements: any | null;
}

export function FacilitatorGuidancePanel({
  guidance,
  loading,
  onRefresh,
  onCopyPrompt,
  currentPhase,
  sessionId,
  transcript,
  sessionAvatars,
  requirements,
}: FacilitatorGuidancePanelProps) {

  const balanceScore = guidance?.conversationHealth?.balanceScore ?? 0;
  const balanceColor = balanceScore >= 7 ? 'text-primary' : balanceScore >= 4 ? 'text-amber-500' : 'text-red-500';
  const barColor = balanceScore >= 7 ? 'bg-primary' : balanceScore >= 4 ? 'bg-amber-500' : 'bg-red-500';
  const engagementLevel = guidance?.conversationHealth?.engagementLevel ?? 'low';
  const engagementBadge = engagementLevel === 'high'
    ? 'bg-green-100 text-green-700 border-green-200'
    : engagementLevel === 'medium'
    ? 'bg-amber-100 text-amber-700 border-amber-200'
    : 'bg-red-100 text-red-700 border-red-200';

  // Guidance content (shared between empty and loaded states)
  const renderGuidanceContent = () => {
    if (!guidance && !loading) {
      return (
        <div className="max-w-[960px] mx-auto flex flex-col gap-6">
          {/* Header */}
          <header className="flex items-center justify-between border-b border-slate-200 pb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <Lightbulb size={22} />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-navy">Facilitator Guidance</h1>
                <p className="text-slate-500 text-sm font-medium">AI-powered session insights</p>
              </div>
            </div>
            <button
              onClick={onRefresh}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
            >
              <RefreshCw size={18} />
              <span>Generate</span>
            </button>
          </header>
          {/* Empty state */}
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Lightbulb size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">Click <strong>Generate</strong> to get AI-powered guidance for your session.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-[960px] mx-auto flex flex-col gap-6">
        {/* Header */}
        <header className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Lightbulb size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Facilitator Guidance</h1>
              <p className="text-slate-500 text-sm font-medium">AI-powered session insights</p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            {guidance && (
              <span className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-navy text-sm font-bold shadow-sm whitespace-nowrap ml-auto md:ml-0">
                {new Date(guidance.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              onClick={onRefresh}
              disabled={loading}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
              <span>{loading ? 'Loading...' : 'Refresh'}</span>
            </button>
          </div>
        </header>

        {/* Loading state */}
        {loading && !guidance ? (
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <Loader2 size={32} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-slate-500 text-sm font-medium">Analyzing session and generating guidance...</p>
          </div>
        ) : guidance ? (
          <>
            {/* Phase Goal Card */}
            <div className="rounded-xl bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 overflow-hidden relative">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4 pl-2">
                <div className="flex items-center gap-3">
                  <Target size={28} className="text-primary" />
                  <div>
                    <h2 className="text-lg font-bold text-navy">{currentPhase.name}</h2>
                    <p className="text-slate-500 text-sm">{currentPhase.goal || currentPhase.objectives}</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-full uppercase tracking-wider">Active</span>
              </div>
              {currentPhase.successCriteria && currentPhase.successCriteria.length > 0 && (
                <div className="pl-2 grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  {currentPhase.successCriteria.map((criteria) => (
                    <label key={criteria} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50 cursor-default">
                      <CheckCircle size={18} className="text-primary shrink-0" />
                      <span className="text-sm font-medium text-slate-700">{criteria}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Summary Card */}
            <div className="rounded-xl bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <Sparkles size={20} className="text-purple-500" />
                <h3 className="text-base font-bold text-navy">Session Summary</h3>
              </div>
              <p className="text-slate-600 leading-relaxed text-sm">{guidance.summary}</p>
            </div>

            {/* Grid: Health + Missing Perspectives */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Conversation Health */}
              <div className="rounded-xl bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 flex flex-col">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-2">
                    <BarChart3 size={20} className="text-slate-400" />
                    <h3 className="text-base font-bold text-navy">Conversation Health</h3>
                  </div>
                  <span className={`px-2.5 py-1 rounded-md text-xs font-bold border capitalize ${engagementBadge}`}>
                    {engagementLevel} Engagement
                  </span>
                </div>
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-500 font-medium">Balance Score</span>
                    <span className={`font-bold ${balanceColor}`}>{balanceScore}/10</span>
                  </div>
                  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                      style={{ width: `${balanceScore * 10}%` }}
                    />
                  </div>
                </div>
                {guidance.conversationHealth.concerns.length > 0 && (
                  <div className="mt-auto flex flex-col gap-2">
                    {guidance.conversationHealth.concerns.map((concern) => (
                      <div key={concern} className="p-3 rounded-lg bg-amber-50 border border-amber-100 flex gap-3 items-start">
                        <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-800 font-medium leading-normal">{concern}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Missing Perspectives */}
              <div className="rounded-xl bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <Users size={20} className="text-slate-400" />
                  <h3 className="text-base font-bold text-navy">Missing Perspectives</h3>
                </div>
                {guidance.missingPerspectives.length === 0 ? (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-100">
                    <CheckCircle size={18} className="text-green-600" />
                    <p className="text-sm text-green-700 font-medium">All perspectives well represented</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {guidance.missingPerspectives.map((mp, idx) => {
                      const avatarInfo = AVATARS[mp.avatar];
                      const avatarName = avatarInfo ? avatarInfo.name : formatRole(mp.avatar);
                      const initials = avatarName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();

                      const borderColor = mp.importance === 'critical' ? 'border-l-red-500' : mp.importance === 'important' ? 'border-l-amber-500' : 'border-l-slate-400';
                      const badgeBg = mp.importance === 'critical' ? 'text-red-500 bg-red-50' : mp.importance === 'important' ? 'text-amber-500 bg-amber-50' : 'text-slate-500 bg-slate-50';
                      const circleBg = mp.importance === 'critical' ? 'bg-red-100 text-red-600' : mp.importance === 'important' ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-600';

                      return (
                        <div key={mp.avatar} className={`flex items-center gap-3 p-3 rounded-lg border-l-4 border border-slate-100 bg-slate-50 ${borderColor}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${circleBg}`}>
                            {avatarInfo?.emoji || initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-center">
                              <p className="font-bold text-sm text-navy">{avatarName}</p>
                              <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${badgeBg}`}>
                                {mp.importance}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5 truncate">{mp.reason}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Suggested Prompts Card */}
            <div className="rounded-xl bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare size={20} className="text-slate-400" />
                <h3 className="text-base font-bold text-navy">Suggested Prompts</h3>
              </div>
              {guidance.suggestedPrompts.length === 0 ? (
                <p className="text-slate-400 text-sm">No specific prompts suggested at this time.</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {guidance.suggestedPrompts.map((prompt) => (
                    <div key={prompt} className="py-3 flex items-center justify-between group">
                      <p className="text-sm text-slate-700 pr-4">"{prompt}"</p>
                      <button
                        onClick={() => onCopyPrompt(prompt)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-xs font-bold text-primary hover:bg-primary/5 px-2 py-1 rounded shrink-0"
                      >
                        <Copy size={14} /> Copy
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Next Step Card */}
            <div className="rounded-xl bg-green-50/50 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-green-100 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500" />
              <div className="flex justify-between items-center pl-2">
                <div>
                  <h3 className="text-base font-bold text-navy mb-1">Recommended Next Step</h3>
                  <p className="text-sm text-slate-600">{guidance.nextStepRecommendation}</p>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    );
  };

  return (
    <div className="flex h-full overflow-hidden">
      {/* Guidance content - scrollable left section */}
      <div className="flex-1 overflow-y-auto min-h-0 p-8">
        {renderGuidanceContent()}
      </div>

      {/* Chat panel - fixed right column, styled as a tile */}
      <div className="w-[400px] shrink-0 p-4 pl-0">
        <div className="h-full rounded-xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 overflow-hidden flex flex-col">
          <FacilitatorChatPanel
            sessionId={sessionId}
            currentPhase={currentPhase}
            transcript={transcript}
            guidance={guidance}
            requirements={requirements}
            sessionAvatars={sessionAvatars}
          />
        </div>
      </div>
    </div>
  );
}
